import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArrowUpRight,
  Check,
  Download,
  Droplet,
  FileText,
  FlaskConical,
  Loader2,
  Mail,
  MapPin,
  Package,
  Phone,
  Send,
  Syringe,
  XCircle,
  AlertTriangle,
  type LucideIcon
} from 'lucide-react';
import { formatDateTime } from '../utils/dateUtils';
import { formatCurrency } from '../lib/format';
import { dtpPath } from '../lib/clinicSite';

type LabRequisitionType = 'at_home_kit' | 'walk_in' | 'mobile_phleb';

interface PaymentSuccessData {
  orderId: string;
  orderNumber?: string;
  paymentIntentId: string;
  transactionId: string;
  patientName: string;
  clinicName: string;
  clinicPhone?: string;
  clinicEmail?: string;
  items: Array<{
    productName: string;
    quantity: number;
    totalPrice: number;
    isLab?: boolean;
  }>;
  subtotal?: number;
  shippingAmount?: number;
  telemedNetworkFee?: number;
  totalAmount: number;
  currency: string;
  paymentStatus: string;
  paymentMethod: {
    type: string;
    last4?: string;
    brand?: string;
  };
  paidAt: string;
  pharmacyName?: string;
  pharmacyInfo?: string;
  labTests?: Array<{
    name: string;
    requisitionType: LabRequisitionType;
    fasting: boolean;
    turnaroundDays: number | null;
  }>;
  physicianId?: string | null;
}

/** The card provider sends the patient here the moment the charge clears, but
 *  the order is only marked paid when its webhook lands a few seconds later.
 *  Keep asking for a while rather than telling someone who just paid that
 *  they haven't. */
const CONFIRM_POLL_MS = 2000;
const CONFIRM_POLL_ATTEMPTS = 15;

interface Step { Icon: LucideIcon; title: string; body: React.ReactNode }

function labSteps(type: LabRequisitionType, fasting: boolean, turnaroundDays: number | null): Step[] {
  const fastingNote = fasting
    ? <> <strong className="font-semibold text-[var(--portal-ink)]">This test requires fasting</strong>, so follow the fasting instructions before you collect.</>
    : null;
  const results: Step = {
    Icon: FileText,
    title: 'Get your results',
    body: <>Your results appear in your patient portal as soon as they’re ready{turnaroundDays ? <>, usually about {turnaroundDays} {turnaroundDays === 1 ? 'day' : 'days'} after the lab receives your sample</> : null}.</>
  };
  if (type === 'walk_in') {
    return [
      { Icon: FlaskConical, title: 'Your order goes to the lab', body: 'We send your lab order electronically. There’s nothing to print.' },
      { Icon: MapPin, title: 'Visit a patient service center', body: <>Stop by a patient service center to give your sample.{fastingNote}</> },
      { Icon: Droplet, title: 'The lab runs your tests', body: 'Your sample is processed at the lab.' },
      results
    ];
  }
  if (type === 'mobile_phleb') {
    return [
      { Icon: FlaskConical, title: 'Your order goes to the lab', body: 'We send your lab order and arrange a mobile blood draw.' },
      { Icon: Syringe, title: 'A phlebotomist comes to you', body: <>They’ll contact you to schedule a time that works.{fastingNote}</> },
      { Icon: Droplet, title: 'The lab runs your tests', body: 'Your sample goes straight to the lab.' },
      results
    ];
  }
  return [
    { Icon: Package, title: 'Your kit ships to you', body: 'The lab mails your collection kit to the address on your order. Keep an eye on your mailbox.' },
    { Icon: Droplet, title: 'Collect your sample', body: <>Follow the instructions inside the kit.{fastingNote}</> },
    { Icon: Send, title: 'Send it back', body: 'Pack your sample as the kit shows and mail it back to the lab.' },
    results
  ];
}

const HEADLINES: Record<LabRequisitionType, { title: string; lede: string }> = {
  at_home_kit: { title: 'Your at-home lab kit is on the way', lede: 'Here’s how it works from here: collect your sample at home, send it back, and get your results.' },
  walk_in: { title: 'Your lab order is placed', lede: 'Here’s how it works from here: visit a lab, give your sample, and get your results.' },
  mobile_phleb: { title: 'Your lab order is placed', lede: 'Here’s how it works from here: a phlebotomist comes to you, and you get your results.' }
};

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="patient-portal min-h-screen">
      <main className="mx-auto w-full max-w-3xl px-4 pb-16 pt-10 sm:px-6 sm:pt-16">{children}</main>
    </div>
  );
}

function StatusCard({ tone, Icon, title, children }: { tone: 'busy' | 'warn' | 'error'; Icon: LucideIcon; title: string; children: React.ReactNode }) {
  const iconTone = tone === 'error' ? 'bg-[#fde4dc] text-[#8c2a14]' : tone === 'warn' ? 'bg-[var(--portal-tint-2)]' : 'bg-[var(--portal-tint-1)]';
  return (
    <Shell>
      <section className="portal-card mx-auto max-w-md p-8 text-center" role={tone === 'busy' ? 'status' : 'alert'}>
        <span aria-hidden="true" className={`mx-auto grid h-14 w-14 place-items-center rounded-2xl ${iconTone}`}>
          <Icon className={`h-6 w-6 ${tone === 'busy' ? 'motion-safe:animate-spin' : ''}`} />
        </span>
        <h1 className="portal-display mt-5 text-2xl font-semibold">{title}</h1>
        <div className="mt-2 text-[var(--portal-muted)]">{children}</div>
      </section>
    </Shell>
  );
}

export const PaymentSuccess: React.FC = () => {
  const { paymentLinkId } = useParams<{ paymentLinkId: string }>();
  const [paymentData, setPaymentData] = useState<PaymentSuccessData | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = async (attempt: number) => {
      if (!paymentLinkId) {
        setError('Invalid payment reference');
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(`/api/payments/success/${paymentLinkId}`);
        if (!response.ok) throw new Error('Payment information not found');
        const body = await response.json();
        if (!body.success || !body.data) throw new Error('Invalid payment data format');
        if (cancelled) return;
        const data = body.data as PaymentSuccessData;
        setPaymentData(data);
        const stillConfirming = data.paymentStatus !== 'succeeded' && data.paymentStatus !== 'failed' && attempt < CONFIRM_POLL_ATTEMPTS;
        setConfirming(stillConfirming);
        if (stillConfirming) timer = setTimeout(() => load(attempt + 1), CONFIRM_POLL_MS);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load payment information');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load(0);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [paymentLinkId]);

  const telemedFee = paymentData?.telemedNetworkFee ?? 0;
  const shipping = paymentData?.shippingAmount ?? 0;

  const handleDownloadReceipt = () => {
    if (!paymentData) return;

    const receiptDate = formatDateTime(paymentData.paidAt, { month: 'long' });
    const pharmacyName = paymentData.pharmacyName || 'Bloom Pharmacy';
    const isLabOnly = paymentData.items.length > 0 && paymentData.items.every(item => item.isLab);

    const cardDisplay = paymentData.paymentMethod.brand && paymentData.paymentMethod.last4
      ? `${paymentData.paymentMethod.brand.toUpperCase()} •••• ${paymentData.paymentMethod.last4}`
      : paymentData.paymentMethod.last4
        ? `Card •••• ${paymentData.paymentMethod.last4}`
        : 'Card payment';

    const receiptHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Payment Receipt - ${pharmacyName}</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { text-align: center; border-bottom: 2px solid #22c55e; padding-bottom: 20px; margin-bottom: 20px; }
          .header h1 { color: #22c55e; margin: 0; font-size: 28px; }
          .header .pharmacy { font-size: 14px; color: #666; margin-top: 5px; }
          .section { margin-bottom: 20px; }
          .section h2 { font-size: 14px; color: #666; margin-bottom: 10px; border-bottom: 1px solid #eee; padding-bottom: 5px; }
          .row { display: flex; justify-content: space-between; margin-bottom: 8px; }
          .label { color: #666; }
          .value { font-weight: 500; }
          .mono { font-family: monospace; font-size: 12px; word-break: break-all; }
          .total { font-size: 18px; font-weight: bold; border-top: 2px solid #000; padding-top: 10px; margin-top: 10px; }
          .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; color: #666; font-size: 12px; }
          .charge-notice { background: #f0fdf4; border: 1px solid #22c55e; border-radius: 8px; padding: 12px; margin-bottom: 20px; text-align: center; }
          .charge-notice strong { color: #166534; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${pharmacyName}</h1>
          <p class="pharmacy">${isLabOnly ? 'Lab Test Payment Receipt' : 'Prescription Payment Receipt'}</p>
          <p style="color: #22c55e; margin-top: 10px; font-weight: 600;">Payment Successful</p>
        </div>

        <div class="charge-notice">
          <strong>This charge will appear on your statement as "${pharmacyName}"</strong>
        </div>

        <div class="section">
          <h2>Transaction Details</h2>
          <div class="row"><span class="label">Transaction ID:</span><span class="value mono">${paymentData.transactionId}</span></div>
          <div class="row"><span class="label">Order ID:</span><span class="value mono">${paymentData.orderId}</span></div>
          <div class="row"><span class="label">Date:</span><span class="value">${receiptDate}</span></div>
          <div class="row"><span class="label">Payment Method:</span><span class="value">${cardDisplay}</span></div>
        </div>

        <div class="section">
          <h2>Patient & Provider</h2>
          <div class="row"><span class="label">Patient:</span><span class="value">${paymentData.patientName}</span></div>
          <div class="row"><span class="label">${isLabOnly ? 'Ordering Provider' : 'Prescribing Provider'}:</span><span class="value">${paymentData.clinicName}</span></div>
          ${isLabOnly ? '' : `<div class="row"><span class="label">Fulfilled by:</span><span class="value">${pharmacyName}</span></div>`}
        </div>

        <div class="section">
          <h2>Order Summary</h2>
          ${paymentData.items.map(item => `
            <div class="row">
              <span class="label">${item.productName} (x${item.quantity})</span>
              <span class="value">${formatCurrency(item.totalPrice)}</span>
            </div>
          `).join('')}
          ${shipping > 0 ? `<div class="row"><span class="label">Shipping</span><span class="value">${formatCurrency(shipping)}</span></div>` : ''}
          ${telemedFee > 0 ? `<div class="row"><span class="label">Telemed visit fee</span><span class="value">${formatCurrency(telemedFee)}</span></div>` : ''}
          <div class="row total">
            <span>Total Paid</span>
            <span>${formatCurrency(paymentData.totalAmount)} ${paymentData.currency}</span>
          </div>
        </div>

        <div class="footer">
          <p>Thank you for your payment.</p>
          <p>Questions about your order? Contact ${paymentData.clinicName}${paymentData.clinicPhone ? ` at ${paymentData.clinicPhone}` : ''}</p>
          <p style="margin-top: 10px; font-size: 11px;">Processed by ${pharmacyName}</p>
        </div>
      </body>
      </html>
    `;

    const blob = new Blob([receiptHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);

    // Use an iframe for printing (safer than document.write)
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = url;

    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(url);
        }, 1000);
      }, 250);
    };

    document.body.appendChild(iframe);
  };

  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleEmailReceipt = async () => {
    if (!paymentData || emailSending) return;

    setEmailSending(true);
    setEmailError(null);

    try {
      const response = await fetch(`/api/payments/receipt/${paymentLinkId}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        setEmailSent(true);
      } else {
        setEmailError(data.error || 'Failed to send receipt email');
      }
    } catch (err) {
      setEmailError('Failed to send receipt email. Please try again.');
      console.error('Error emailing receipt:', err);
    } finally {
      setEmailSending(false);
    }
  };

  if (loading || confirming) {
    return (
      <StatusCard tone="busy" Icon={Loader2} title={confirming ? 'Confirming your payment…' : 'Loading your confirmation…'}>
        {confirming ? 'This only takes a few seconds. Please keep this page open.' : null}
      </StatusCard>
    );
  }

  if (error || !paymentData) {
    return (
      <StatusCard tone="error" Icon={FileText} title="We couldn’t load your confirmation">
        <p>{error}</p>
      </StatusCard>
    );
  }

  if (paymentData.paymentStatus !== 'succeeded') {
    const isFailed = paymentData.paymentStatus === 'failed';
    return (
      <StatusCard tone={isFailed ? 'error' : 'warn'} Icon={isFailed ? XCircle : AlertTriangle} title={isFailed ? 'Your payment didn’t go through' : 'We’re still confirming your payment'}>
        <p>
          {isFailed
            ? 'Your card was declined. Please try again with a different payment method.'
            : 'Your bank hasn’t confirmed the charge yet. Please don’t pay again. Refresh this page in a minute, or contact your clinic if it doesn’t update.'}
        </p>
        {paymentData.clinicPhone && (
          <a href={`tel:${paymentData.clinicPhone}`}
            className="mt-6 inline-flex h-11 items-center gap-2 rounded-full bg-white/80 px-5 text-sm font-semibold text-[var(--portal-ink)] ring-1 ring-[var(--portal-line)] hover:bg-white">
            <Phone aria-hidden="true" className="h-4 w-4" />Contact {paymentData.clinicName}
          </a>
        )}
      </StatusCard>
    );
  }

  const rawFirst = paymentData.patientName === 'Unknown Patient' ? '' : paymentData.patientName.split(' ')[0] || '';
  const firstName = rawFirst ? rawFirst.charAt(0).toUpperCase() + rawFirst.slice(1).toLowerCase() : '';
  const labTests = paymentData.labTests ?? [];
  const lab = labTests[0];
  const hasRxItems = paymentData.items.some(item => !item.isLab);
  const steps = lab
    ? labSteps(lab.requisitionType, labTests.some(t => t.fasting), lab.turnaroundDays)
    : [
      { Icon: Package, title: 'The pharmacy prepares your order', body: 'Your prescription is filled and checked before it ships.' },
      { Icon: Send, title: 'It ships to you', body: 'We’ll text you tracking as soon as it’s on the way.' }
    ];
  const headline = lab ? HEADLINES[lab.requisitionType] : { title: 'Your order is confirmed', lede: 'Thanks for your payment. Your prescription is on its way to the pharmacy.' };
  const portalUrl = paymentData.physicianId ? dtpPath(paymentData.physicianId, lab ? '/results' : '/orders') : null;

  return (
    <Shell>
      {/* Hero */}
      <header className="text-center">
        <span aria-hidden="true" className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-[var(--portal-accent)] text-[var(--portal-accent-fg)] shadow-[0_18px_40px_-18px_rgba(224,86,58,0.7)]">
          <Check className="h-8 w-8" strokeWidth={2.5} />
        </span>
        <p className="mt-6 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--portal-muted)]">
          Payment received · {formatCurrency(paymentData.totalAmount)}
        </p>
        <h1 className="portal-display mt-2 text-3xl font-semibold sm:text-4xl">
          {firstName ? `${firstName}, ${headline.title.charAt(0).toLowerCase()}${headline.title.slice(1)}` : headline.title}
        </h1>
        <p className="mx-auto mt-3 max-w-lg text-[var(--portal-muted)]">{headline.lede}</p>
      </header>

      {/* What happens next */}
      <section aria-labelledby="next-steps" className="portal-card mt-10 p-6 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="next-steps" className="portal-display text-xl font-semibold">What happens next</h2>
          {labTests.length > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--portal-tint-3)] px-3 py-1 text-xs font-semibold">
              <FlaskConical aria-hidden="true" className="h-3.5 w-3.5" />
              {labTests.length === 1 ? labTests[0].name : `${labTests.length} lab tests`}
            </span>
          )}
        </div>
        <ol className="mt-6">
          {steps.map((step, index) => {
            const last = index === steps.length - 1;
            return (
              <li key={step.title} className="relative flex gap-4 pb-7 last:pb-0">
                {!last && <span aria-hidden="true" className="absolute left-[21px] top-12 bottom-1 w-px bg-[var(--portal-line)]" />}
                <span aria-hidden="true" className={`relative grid h-11 w-11 shrink-0 place-items-center rounded-2xl ${index === 0 ? 'bg-[var(--portal-tint-1)]' : last ? 'bg-[var(--portal-tint-2)]' : 'bg-white/80 ring-1 ring-[var(--portal-line)]'}`}>
                  <step.Icon className="h-5 w-5" />
                </span>
                <div className="min-w-0 pt-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--portal-muted)]">Step {index + 1}</p>
                  <h3 className="mt-0.5 font-semibold">{step.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-[var(--portal-muted)]">{step.body}</p>
                </div>
              </li>
            );
          })}
        </ol>
        {lab && hasRxItems && (
          <p className="mt-6 rounded-2xl bg-white/70 p-4 text-sm ring-1 ring-[var(--portal-line)]">
            The other items in your order are prepared by the pharmacy and ship separately. We’ll text you tracking.
          </p>
        )}
        {portalUrl && (
          <a href={portalUrl}
            className="mt-7 inline-flex h-11 items-center gap-2 rounded-full bg-[var(--portal-primary)] px-5 text-sm font-semibold text-[var(--portal-primary-fg)] transition hover:opacity-90">
            {lab ? 'Track it in your patient portal' : 'View your order'}<ArrowUpRight aria-hidden="true" className="h-4 w-4" />
          </a>
        )}
      </section>

      {/* Summary */}
      <section aria-labelledby="order-summary" className="portal-card mt-6 p-6 sm:p-8">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="order-summary" className="portal-display text-xl font-semibold">Order summary</h2>
          <p className="text-sm text-[var(--portal-muted)]">{formatDateTime(paymentData.paidAt, { month: 'long' })}</p>
        </div>
        <ul className="mt-5 divide-y divide-[var(--portal-line)]">
          {paymentData.items.map((item, index) => (
            <li key={index} className="flex items-start justify-between gap-4 py-3">
              <div className="min-w-0">
                <p className="font-medium">{item.productName}</p>
                <p className="text-sm text-[var(--portal-muted)]">{item.isLab ? 'Lab test' : `Qty ${item.quantity}`}</p>
              </div>
              <p className="font-medium tabular-nums">{formatCurrency(item.totalPrice)}</p>
            </li>
          ))}
        </ul>
        <dl className="mt-2 space-y-2 border-t border-[var(--portal-line)] pt-4 text-sm">
          {shipping > 0 && <div className="flex justify-between"><dt className="text-[var(--portal-muted)]">Shipping</dt><dd className="tabular-nums">{formatCurrency(shipping)}</dd></div>}
          {telemedFee > 0 && <div className="flex justify-between"><dt className="text-[var(--portal-muted)]">Telemed visit fee</dt><dd className="tabular-nums">{formatCurrency(telemedFee)}</dd></div>}
          <div className="flex justify-between pt-1 text-base font-semibold"><dt>Total paid</dt><dd className="tabular-nums">{formatCurrency(paymentData.totalAmount)}</dd></div>
        </dl>
        <p className="mt-4 text-sm text-[var(--portal-muted)]">
          {paymentData.paymentMethod.brand && paymentData.paymentMethod.last4
            ? `${paymentData.paymentMethod.brand.toUpperCase()} •••• ${paymentData.paymentMethod.last4}`
            : paymentData.paymentMethod.last4 ? `Card ending in ${paymentData.paymentMethod.last4}` : 'Paid by card'}
          {' · '}Ordered by {paymentData.clinicName}
          {paymentData.orderNumber && <> · {paymentData.orderNumber}</>}
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          <button type="button" onClick={handleDownloadReceipt}
            className="inline-flex h-10 items-center gap-2 rounded-full bg-white/80 px-4 text-sm font-semibold ring-1 ring-[var(--portal-line)] transition hover:bg-white">
            <Download aria-hidden="true" className="h-4 w-4" />Download receipt
          </button>
          <button type="button" onClick={handleEmailReceipt} disabled={emailSending || emailSent}
            className="inline-flex h-10 items-center gap-2 rounded-full bg-white/80 px-4 text-sm font-semibold ring-1 ring-[var(--portal-line)] transition hover:bg-white disabled:cursor-default disabled:opacity-80">
            {emailSending ? <Loader2 aria-hidden="true" className="h-4 w-4 motion-safe:animate-spin" /> : emailSent ? <Check aria-hidden="true" className="h-4 w-4" /> : <Mail aria-hidden="true" className="h-4 w-4" />}
            {emailSending ? 'Sending…' : emailSent ? 'Receipt sent' : 'Email receipt'}
          </button>
          {paymentData.clinicPhone && (
            <a href={`tel:${paymentData.clinicPhone}`}
              className="inline-flex h-10 items-center gap-2 rounded-full bg-white/80 px-4 text-sm font-semibold ring-1 ring-[var(--portal-line)] transition hover:bg-white">
              <Phone aria-hidden="true" className="h-4 w-4" />Call {paymentData.clinicName}
            </a>
          )}
        </div>
        {emailError && <p role="alert" className="mt-3 text-sm text-[#8c2a14]">{emailError}</p>}
      </section>

      <p className="mt-8 text-center text-xs text-[var(--portal-muted)]">
        Questions? Contact {paymentData.clinicName}
        {paymentData.clinicPhone && <> at <a className="underline" href={`tel:${paymentData.clinicPhone}`}>{paymentData.clinicPhone}</a></>}
        {paymentData.clinicEmail && <> or <a className="underline" href={`mailto:${paymentData.clinicEmail}`}>{paymentData.clinicEmail}</a></>}.
      </p>
    </Shell>
  );
};
